import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatChipListbox, MatChipOption } from '@angular/material/chips';
import { MatDialog } from '@angular/material/dialog';
import { MatTooltip } from '@angular/material/tooltip';
import { PushPipe } from '@ngrx/component';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { filter, map, merge, Observable, of, Subject, switchMap } from 'rxjs';

import { RoleCode } from '@ninety/platform/models/_shared/role-code';
import { Scopes } from '@ninety/platform/models/entitlements/entitlements-model';
import { FilterService, StateService } from '@ninety/platform/services';
import {
  ToolbarBackButtonEvents,
  FeatureFlagFacade,
  FeatureFlagKeys,
  LogOutActions,
  selectCompanySettings,
  selectCurrentPerson,
  selectFeatureEnabled,
  NavigationActions,
  selectConversationChannel,
  selectRouteParam,
  CurrentUserSelectors,
  selectCurrentUserId,
  selectCurrentUserIsLiteUser,
  selectCurrentUserIsManageeOrAbove,
  selectCurrentUserIsObserver,
  selectUserAvatarInfo,
  selectLanguage,
  selectDoesUserHaveAccessToOrgChart,
} from '@ninety/platform/state';
import { selectAuthShouldHaveAccess } from '@ninety/platform/state/app-global/auth/auth.selectors';
import {
  TerraAvatarModule,
  TerraBadgeModule,
  TerraDividerModule,
  TerraHtmlSanitizerModule,
  TerraIconModule,
} from '@ninety/terra';
import { ContactCardHoverDirective, StopPropagationDirective, ButtonComponent } from '@ninety/ui/core';
import { UserService } from '@ninety/ui/legacy/core/services/user.service';
import { environment } from '@ninety/ui/web/environments';
import { InboxTriggerComponent } from '@ninety/web/pages/inbox/components/inbox-trigger/inbox-trigger.component';
import { MazAskButtonComponent } from '@ninety/web/pages/maz';
import { MeetingService } from '@ninety/web/pages/meeting/_shared/services/meeting.service';
import { MeetingStateActions } from '@ninety/web/pages/meeting/_state/meetings.actions';
import { MeetingsStateSelectors } from '@ninety/web/pages/meeting/_state/meetings.selectors';
import { NotificationFeedTriggerComponent } from '@ninety/web/pages/notification-feed/components/notification-feed-trigger/notification-feed-trigger.component';

import * as TopToolbarSelectors from '../../_state/top-toolbar/top-toolbar-state.selectors';
import { UniversalCreateActions } from '../../_state/universal-create/universal-create.actions';
import { GridLayoutSelectors } from '../../grid-layout/_state/grid-layout-state.selectors';
import { CreateDialogService } from '../../services/create-dialog.service';
import { GlobalSearchInputComponent } from '../global-search-input/global-search-input.component';
import { HelpDialogComponent } from '../help-dialog/help-dialog.component';
import { NotetakerControlComponent } from '../notetaker-control/notetaker-control.component';
import { UniversalCreateBtnComponent } from '../universal-create/universal-create-btn/universal-create-btn.component';

import { PageDescriptionPipe } from './page-description.pipe';

@Component({
  selector: 'ninety-top-toolbar',
  templateUrl: './top-toolbar.component.html',
  styleUrls: ['./top-toolbar.component.scss', './top-toolbar.terrafy.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    MatIconButton,
    TerraIconModule,
    MatTooltip,
    TerraDividerModule,
    MatChipListbox,
    MatChipOption,
    TerraAvatarModule,
    TerraBadgeModule,
    ContactCardHoverDirective,
    StopPropagationDirective,
    ButtonComponent,
    InboxTriggerComponent,
    NotificationFeedTriggerComponent,
    UniversalCreateBtnComponent,
    GlobalSearchInputComponent,
    AsyncPipe,
    TerraHtmlSanitizerModule,
    PushPipe,
    PageDescriptionPipe,
    NotetakerControlComponent,
    MazAskButtonComponent,
    NgTemplateOutlet,
  ],
})
export class TopToolbarComponent implements OnInit, OnDestroy {
  readonly filterService = inject(FilterService);
  private readonly legacyDialog = inject(MatDialog);
  readonly meetingService = inject(MeetingService);
  readonly stateService = inject(StateService);
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly userService = inject(UserService);
  readonly createDialogService = inject(CreateDialogService);
  private readonly featureFlags = inject(FeatureFlagFacade);
  readonly store = inject(Store);

  @Input() meetingStarted?: boolean;

  @Output() menuClick = new EventEmitter<any>();

  pageTitle$: Observable<string>;
  participants$: Observable<{ userIds: string[]; usernames: string[] }>;
  isEditing$ = this.store.select(GridLayoutSelectors.selectIsLayoutModeEnabled);

  environment = environment; // just for checking in settings link
  contextMenuPosition = { x: '0px', y: '0px' };
  readonly NUM_OF_PARTICIPANTS_LARGE_SCREEN = 4;
  readonly NUM_OF_PARTICIPANTS_MEDIUM_SCREEN = 1;

  protected readonly companySettings$ = this.store.select(selectCompanySettings);
  protected readonly currentPerson$ = this.store.select(selectCurrentPerson);
  protected readonly currentUserId$ = this.store.select(selectCurrentUserId);
  protected readonly disableNavLinks$ = this.store
    .select(selectAuthShouldHaveAccess)
    .pipe(map(shouldHaveAccess => !shouldHaveAccess));
  protected readonly enableSurveys$ = this.featureFlags.getFlag(FeatureFlagKeys.enableAssessments);
  private readonly isLiteUser$ = this.store.select(selectCurrentUserIsLiteUser);
  protected readonly isNotLiteUser$ = this.isLiteUser$.pipe(map(isLiteUser => !isLiteUser));
  protected readonly isManageeOrAbove$ = this.store.select(selectCurrentUserIsManageeOrAbove);
  protected readonly isObserver$ = this.store.select(selectCurrentUserIsObserver);
  protected readonly language$ = this.store.select(selectLanguage);
  protected readonly mainToolbarHasShadow$ = this.store.select(TopToolbarSelectors.selectMainToolbarHasShadow);
  protected readonly mainToolbarHidden$ = this.store.select(TopToolbarSelectors.selectMainToolbarHidden);
  protected readonly canAccessOrgChart$ = this.store.select(selectDoesUserHaveAccessToOrgChart);
  protected readonly userTeams$ = this.store.select(CurrentUserSelectors.selectTeams);
  protected readonly currentMeeting$ = this.store.select(MeetingsStateSelectors.selectCurrentMeeting);
  protected readonly segueEnhancements$ = this.featureFlags.getFlag(FeatureFlagKeys.webSegueEnhancements);
  /**
   * DEV-27227: the meeting sidenav marks the facilitator's section, so this avatar is redundant.
   * Uses the composed selector, not the raw flag — otherwise this would drop the avatar in the
   * combination where the sidenav suppresses its replacement.
   */
  protected readonly autofollowFacilitator$ = this.store.select(
    MeetingsStateSelectors.selectAutofollowPresenterEnabled
  );
  protected readonly webGlobalSearchEnabled$ = this.featureFlags.getFlag(FeatureFlagKeys.webGlobalSearch);
  protected readonly toolbarBackButton$ = this.store.select(TopToolbarSelectors.selectToolbarBackButton);

  protected readonly currentConversationMeeting$ = this.store.select(selectRouteParam('id')).pipe(
    filter(id => !!id),
    switchMap(id => this.store.select(selectConversationChannel(id)))
  );

  protected readonly universalCreateFeature$ = this.store.select(selectFeatureEnabled(Scopes.universalCreate));

  selectUserAvatarInfo = selectUserAvatarInfo;

  chipMessage$ = this.store.select(TopToolbarSelectors.selectChipMessage);

  private readonly destroy$: Subject<void> = new Subject<void>();

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit() {
    this.pageTitle$ = this.stateService.pageTitle$.pipe(
      switchMap(title => {
        if (this.meetingStarted && title.toLowerCase().includes('data')) return of(title.replace('Data', 'Scorecard'));

        if (title.toLowerCase().includes('v/to')) return of('Vision/Traction Organizer™');

        return of(title);
      })
    );

    this.participants$ = merge(
      this.breakpointObserver.observe(['(min-width: 375px)', '(min-width: 768px)', '(min-width: 1024px)']).pipe(
        switchMap((result: BreakpointState) => {
          const { breakpoints } = result;

          if (breakpoints['(min-width: 1024px)']) {
            return of(this.NUM_OF_PARTICIPANTS_LARGE_SCREEN);
          }

          if (breakpoints['(min-width: 768px)']) {
            return of(this.NUM_OF_PARTICIPANTS_MEDIUM_SCREEN);
          }

          return of(0);
        })
      ),
      this.meetingService.presenceChanged$.pipe(
        switchMap(() => {
          if (this.breakpointObserver.isMatched(['(min-width: 1024px)'])) {
            return of(this.NUM_OF_PARTICIPANTS_LARGE_SCREEN);
          }

          if (this.breakpointObserver.isMatched(['(min-width: 768px)'])) {
            return of(this.NUM_OF_PARTICIPANTS_MEDIUM_SCREEN);
          }

          return of(0);
        })
      )
    ).pipe(
      concatLatestFrom(() => [
        this.store.select(MeetingsStateSelectors.selectCurrentMeeting),
        this.store.select(MeetingsStateSelectors.selectFilteredMeetingAttendees),
      ]),
      filter(([, meeting]) => meeting?.inProgress),
      map(([maxAvatarCount, _, { attendees }]) => {
        if (maxAvatarCount === 0) {
          return { userIds: [], usernames: [], isObserver: [] };
        } else {
          const userIds = attendees.slice(0, maxAvatarCount);
          const usernames =
            attendees.length > maxAvatarCount ? attendees.slice(maxAvatarCount).map(id => this.getUsername(id)) : [];
          return { userIds, usernames };
        }
      })
    );
  }

  logout() {
    this.store.dispatch(LogOutActions.initiateLogOut());
  }

  onBackButtonClick(source: string): void {
    this.store.dispatch(ToolbarBackButtonEvents.clicked({ source }));
  }

  getUsername(id: string) {
    const user = this.userService?.users?.find(u => u._id === id);
    if (!user) return '';

    const { name } = user.metadata;
    if (name?.first || name?.last) return `${name?.first} ${name?.last}`.trim();

    if (user.emailAddresses?.length) return user.emailAddresses[0].email;

    return 'Unknown User';
  }

  isObserver(id: string) {
    const user = this.userService?.users?.find(u => u._id === id);
    if (!user) return true; // Unknown user treated as observer

    return user.roleCode === RoleCode.observer;
  }

  openUniversalCreateModal(): void {
    this.store.dispatch(UniversalCreateActions.globalCreateButtonClicked());
    this.createDialogService.open().subscribe();
  }

  openHelpModal() {
    this.legacyDialog.open(HelpDialogComponent, {
      autoFocus: false,
    });
  }

  openChangePresenterDialog() {
    this.store.dispatch(MeetingStateActions.openClaimPresenterDialog());
  }

  openMenu() {
    this.store.dispatch(NavigationActions.openHamburgerMenu());
  }
}
